import { FunctionComponent, memo, useLayoutEffect, useRef, useState } from 'react';

import { domainLogo } from '../domain-logo';

const spinnerCircleClassName = 'animate-spin rounded-full border-2 border-button-primary border-t-transparent';

/** Inline spinner. Use for buttons, cells, or inline loading. */
export const InlineSpinner: FunctionComponent<{ className?: string }> = memo(({ className = 'h-12 w-12' }) => (
  <div className={`${spinnerCircleClassName} ${className}`} aria-hidden />
));

type SpinnerProps = {
  exiting?: boolean;
  /**
   * undefined: play the intro from now.
   * null: keep the logo hidden until the boot splash is allowed to start.
   * number: play from that timestamp so a remount does not restart at frame zero.
   */
  introStartedAt?: number | null;
};

/** Full-page logo splash. Stays up across the login handoff, then fades into the page. */
export const Spinner: FunctionComponent<SpinnerProps> = memo(({ exiting = false, introStartedAt }) => {
  const immediate = introStartedAt === undefined;
  const [immediateStart] = useState(() => (immediate ? Date.now() : null));
  const startAt = immediate ? immediateStart : introStartedAt;
  const logoRef = useRef<HTMLImageElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const [introArmed, setIntroArmed] = useState(false);
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useLayoutEffect(() => {
    if (startAt == null || reduceMotion) return;

    const elapsed = Math.max(0, Date.now() - startAt);
    // Inline delay survives a stylesheet swap, which otherwise restarts the intro at frame zero.
    if (logoRef.current) logoRef.current.style.animationDelay = `-${elapsed}ms`;
    if (lineRef.current) lineRef.current.style.animationDelay = `calc(-${elapsed}ms + 0.4s)`;
    setIntroArmed(true);
  }, [reduceMotion, startAt]);

  const introVisible = startAt != null && (reduceMotion || introArmed);

  return (
    <div
      className={`z-200 fixed inset-0 flex h-full w-full items-center justify-center bg-white ${
        exiting ? 'motion-safe:animate-splash-exit' : ''
      }`}
      role="status"
      aria-live="polite"
      aria-busy={!exiting}
      aria-label="Loading"
    >
      <div className="flex flex-col items-center px-6">
        <img
          ref={logoRef}
          src={domainLogo}
          alt=""
          className={`h-16 w-auto sm:h-20 ${
            introVisible && !reduceMotion ? 'motion-safe:animate-splash-logo' : ''
          } ${introVisible ? '' : 'opacity-0'}`}
        />
        <div className="bg-gray-light mt-8 h-0.5 w-36 overflow-hidden rounded-full">
          <div
            ref={lineRef}
            className={`bg-button-primary h-full w-full origin-center ${
              introVisible && !reduceMotion ? 'motion-safe:animate-splash-line' : 'scale-x-0'
            }`}
          />
        </div>
      </div>
    </div>
  );
});
